import { includes } from 'lodash/fp';
import { PRODUCT, PRODUCT_TYPE } from '@ahmdigital/constants';
import React from 'react';

import { FRAGMENTS } from '../../../src/client/models/table-of-contents/constants';
import { ItemType } from '../../../src/client/pages/home/components/content-tiles/utils/map-content';
import { nextReduxWrapper } from '../../../src/client/store/create-store';
import { ProductTypeType } from '../../../src/client/types/product';
import AcquisitionLandingPage from '../../../src/client/pages/acquisition-landing';
import fetchProductsFromParams from '../../../src/client/models/product/fetch-products-from-params';
import getAnnouncementsQuery from '../../../pages/utils/get-announcements-query';
import getOffersContent from '../../../pages/utils/get-offers-content';
import getShouldSkipClientSideRequest from '../../../src/client/utils/get-should-skip-client-side-request';
import setCacheControlHeader from '../../../pages/utils/set-cache-control-header';
import useGetOffersContent from '../../../pages/utils/use-get-offers-content';
import useGetTableOfContentsLists from '../../../pages/utils/use-get-table-of-contents-lists';
import type { Offer, Placeholder } from '../../../src/client/components/offers/types';

export type AcquisitionLandingPageTemplateProps = {
  activeOffers: Offer[];
  pageContent: {
    item: ItemType;
  }[];
  placeholders: Placeholder[];
  productType?: ProductTypeType;
  slug: string;
};

const AcquisitionLandingPageTemplate = ({
  activeOffers: serverSideOffers,
  pageContent: serverSidePageContent,
  placeholders: serverSidePlaceholders,
  productType,
  slug,
}: AcquisitionLandingPageTemplateProps) => {
  const shouldSkip = getShouldSkipClientSideRequest();
  const { activeOffers, placeholders, loading: isOfferContentLoading } = useGetOffersContent({ shouldSkip });

  const { data: pageContent, loading: isPageContentLoading } = useGetTableOfContentsLists({
    fragment: FRAGMENTS.ANNOUNCEMENTS,
    shouldSkip,
    slug,
  });

  const isLoading = isOfferContentLoading || isPageContentLoading;
  const useServerSideContent = shouldSkip || isLoading;

  const content = useServerSideContent
    ? {
        activeOffers: serverSideOffers,
        pageContent: serverSidePageContent,
        placeholders: serverSidePlaceholders,
      }
    : {
        activeOffers,
        pageContent,
        placeholders,
      };

  return <AcquisitionLandingPage {...content} slug={slug} productType={productType} />;
};

const productParams = {
  productIds: [
    // Extras Only
    PRODUCT.BLACK_60_EXTRAS,
    PRODUCT.FAMILY_EXTRAS,
    PRODUCT.LIFESTYLE_EXTRAS,
    PRODUCT.SUPER_EXTRAS,
    PRODUCT.VALUE_EXTRAS,
    // Hospital Only
    PRODUCT.CORE_BRONZE_PLUS_EXCESS_750,
    PRODUCT.CORE_SILVER_PLUS_EXCESS_750,
    PRODUCT.CORE_HOSPITAL_BASIC_PLUS_EXCESS_750,
    PRODUCT.ESSENTIALS_SILVER_PLUS_EXCESS_750,
    PRODUCT.SIMPLE_BRONZE_PLUS_EXCESS_750,
    PRODUCT.STARTER_BRONZE_EXCESS_750,
    // Combined
    PRODUCT.CORE_HOSPITAL_BASIC_PLUS_EXCESS_750_CHOOSABLE_50_TEETH,
    PRODUCT.AMBULANCE_AND_ACCIDENT_BASIC_EXCESS_750_CHOOSABLE_50_TEETH,
    PRODUCT.SIMPLE_BRONZE_PLUS_EXCESS_750_VALUE_EXTRAS,
    PRODUCT.STARTER_BRONZE_EXCESS_750_VALUE_EXTRAS,
  ],
};

export const getTemplateServerSideProps = ({ slug }: { slug: string }) =>
  nextReduxWrapper.getServerSideProps((store) => async ({ query, res }) => {
    const ALLOWABLE_PRODUCT_TYPES = [PRODUCT_TYPE.EXTRAS, PRODUCT_TYPE.HOSPITAL, PRODUCT_TYPE.PACKAGE];
    const productType = includes(query?.type, ALLOWABLE_PRODUCT_TYPES) ? query.type : PRODUCT_TYPE.PACKAGE;

    setCacheControlHeader(res);

    const [pageContent, { activeOffers, placeholders }, products] = await Promise.all([
      getAnnouncementsQuery(slug),
      getOffersContent(),
      fetchProductsFromParams(store.getState(), store.dispatch, productParams),
    ]);

    return {
      props: {
        activeOffers,
        pageContent,
        placeholders,
        productType,
        products,
        slug,
      },
    };
  });

export default AcquisitionLandingPageTemplate;
